import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createVehicleType,
  getAllVehicleTypes,
  getVehicleTypeByID,
  updateVehicleType,
} from "./services/vehicleType.services";
import { clearVehicleTypeDetail } from "./vehicleTypeSlice";
import { closed } from "../../components/drawer/drawerSlice";

const CreateVehicleType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const VehicleTypeDetail = useAppSelector(
    (state) => state.vehicleType.VehicleTypeDetail
  );
  const VehicleTypeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.vehicleType.formLoading);

  useEffect(() => {
    if (VehicleTypeID) {
      dispatch(getVehicleTypeByID(VehicleTypeID));
    }
  }, [VehicleTypeID]);

  useEffect(() => {
    return () => {
      dispatch(clearVehicleTypeDetail());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!VehicleTypeID) {
        const payload = {
          VehicleType: values.VehicleType,
          IsActive: true,
        };
        const response = await dispatch(createVehicleType(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVehicleTypeDetail());
          dispatch(getAllVehicleTypes());
        }
      } else {
        const payload = {
          ID: VehicleTypeID,
          VehicleType: values.VehicleType,
          IsActive: values.IsActive,
        };
        const response = await dispatch(updateVehicleType(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearVehicleTypeDetail());
          dispatch(getAllVehicleTypes());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={VehicleTypeDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="VehicleType"
                label="Vehicle Type"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={!VehicleTypeID ? "Create" : "Update"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateVehicleType;
